import { useCallback, useRef, useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';

import {
  useDriveConversationsLazyQuery,
  DriveSubscribeToConversationDocument,
  type DriveResponseChunk,
} from '@occc/graphql';

import type {
  UseStreamOrchestratorOptions,
  UseStreamOrchestratorReturn,
  StreamError,
  StreamRecovery,
  StreamScheduler,
  StreamSchedulerHandle,
  WaitingStatus,
} from './use-stream-orchestrator.types';

import { parseResponseContent } from '../../../parse-response-content';
import { ChunkProcessor, type RawChunk } from '../chunk-processor';

const GAP_TIMEOUT_MS = 3000;
const STREAM_STALL_TIMEOUT_MS = 30_000;
const MAX_NO_PROGRESS_CYCLES = 10;
const MESSAGE_ESCALATION_CYCLE = 3;

const defaultScheduler: StreamScheduler = {
  schedule: (task, delayMs) =>
    setTimeout(() => {
      void task();
    }, delayMs),
  cancel: (handle) => {
    clearTimeout(handle as ReturnType<typeof setTimeout>);
  },
};

// Extended type for response with lastChunkNumber (field exists in GraphQL fragment but may not be in generated types)
type ResponseWithChunkNumber = {
  id: string;
  content: string;
  complete: boolean;
  lastChunkNumber?: number | null;
};

type StreamGraphQLError = {
  message?: string;
};

type OrchestratorState = {
  conversationId: string | null;
  responseId: string | null;
  isRecovering: boolean;
  recoveryToken: number | null;
};

export const useStreamOrchestrator = ({
  onContentUpdate,
  onStreamComplete,
  onError,
  onWaiting,
  scheduler = defaultScheduler,
}: UseStreamOrchestratorOptions): UseStreamOrchestratorReturn => {
  const client = useApolloClient();
  const [isStreaming, setIsStreaming] = useState(false);

  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const processorRef = useRef<ChunkProcessor>(new ChunkProcessor());
  const gapTimeoutRef = useRef<StreamSchedulerHandle | null>(null);
  const watchdogTimeoutRef = useRef<StreamSchedulerHandle | null>(null);
  const nextRecoveryTokenRef = useRef(0);
  const noProgressCountRef = useRef(0);
  const stateRef = useRef<OrchestratorState>({
    conversationId: null,
    responseId: null,
    isRecovering: false,
    recoveryToken: null,
  });

  // Keep callback refs stable to avoid stale closures
  const onContentUpdateRef = useRef(onContentUpdate);
  const onStreamCompleteRef = useRef(onStreamComplete);
  const onErrorRef = useRef(onError);
  const onWaitingRef = useRef(onWaiting);

  onContentUpdateRef.current = onContentUpdate;
  onStreamCompleteRef.current = onStreamComplete;
  onErrorRef.current = onError;
  onWaitingRef.current = onWaiting;

  const [refetchConversation] = useDriveConversationsLazyQuery({
    fetchPolicy: 'network-only',
  });

  const getSubscriptionErrorMessage = useCallback((errors: readonly StreamGraphQLError[]) => {
    const messages = errors.map((error) => error.message).filter(Boolean);
    return messages.length > 0 ? messages.join('. ') : 'Subscription error';
  }, []);

  const clearGapTimeout = useCallback(() => {
    if (gapTimeoutRef.current) {
      scheduler.cancel(gapTimeoutRef.current);
      gapTimeoutRef.current = null;
    }
  }, [scheduler]);

  const clearWatchdogTimeout = useCallback(() => {
    if (watchdogTimeoutRef.current) {
      scheduler.cancel(watchdogTimeoutRef.current);
      watchdogTimeoutRef.current = null;
    }
  }, [scheduler]);

  const clearTimers = useCallback(() => {
    clearGapTimeout();
    clearWatchdogTimeout();
  }, [clearGapTimeout, clearWatchdogTimeout]);

  const finalizeActiveResponse = useCallback(() => {
    clearTimers();
    processorRef.current.reset();
    noProgressCountRef.current = 0;
    stateRef.current.responseId = null;
    stateRef.current.isRecovering = false;
    stateRef.current.recoveryToken = null;
    setIsStreaming(false);
  }, [clearTimers]);

  const stopStream = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    clearTimers();
    processorRef.current.reset();
    noProgressCountRef.current = 0;
    stateRef.current.conversationId = null;
    stateRef.current.responseId = null;
    stateRef.current.isRecovering = false;
    stateRef.current.recoveryToken = null;

    setIsStreaming(false);
  }, [clearTimers]);

  const handleStalledTimeoutRef = useRef<() => void>();
  handleStalledTimeoutRef.current = () => {
    const state = stateRef.current;
    if (!state.responseId) {
      return;
    }

    noProgressCountRef.current += 1;

    if (noProgressCountRef.current >= MAX_NO_PROGRESS_CYCLES) {
      stopStream();
      onErrorRef.current({
        type: 'stalled',
        message: 'Response generation appears to have stalled. Please try again.',
      });
    } else {
      const status: WaitingStatus =
        noProgressCountRef.current <= MESSAGE_ESCALATION_CYCLE ? 'working' : 'complex';
      onWaitingRef.current(status);
      watchdogTimeoutRef.current = scheduler.schedule(
        () => handleStalledTimeoutRef.current?.(),
        STREAM_STALL_TIMEOUT_MS,
      );
    }
  };

  const resetWatchdog = useCallback(() => {
    const state = stateRef.current;
    if (!state.responseId) {
      return;
    }

    noProgressCountRef.current = 0;
    clearWatchdogTimeout();
    watchdogTimeoutRef.current = scheduler.schedule(
      () => handleStalledTimeoutRef.current?.(),
      STREAM_STALL_TIMEOUT_MS,
    );
  }, [clearWatchdogTimeout, scheduler]);

  const handleGapRecovery = useCallback(async () => {
    const state = stateRef.current;

    if (state.isRecovering || !state.conversationId || !state.responseId) {
      return;
    }

    // Capture the responseId we're recovering for. If it changes during the
    // async refetch (e.g. user sent a new prompt), we must discard this result
    // to avoid corrupting the new stream's processor state.
    const recoveringResponseId = state.responseId;
    const recoveryToken = ++nextRecoveryTokenRef.current;

    state.isRecovering = true;
    state.recoveryToken = recoveryToken;

    try {
      const result = await refetchConversation({
        variables: { filter: { idEquals: state.conversationId } },
      });

      // Bail out if the stream moved on while we were fetching.
      if (state.responseId !== recoveringResponseId) {
        return;
      }

      const conversation = result.data?.driveConversations?.edges?.[0]?.node;
      const response = conversation?.responses?.find((r) => r.id === recoveringResponseId) as
        | ResponseWithChunkNumber
        | undefined;

      if (!response) {
        Sentry.captureMessage('[StreamOrchestrator] Response not found in recovery refetch', {
          level: 'warning',
          tags: {
            conversationId: state.conversationId,
            responseId: state.responseId ?? 'unknown',
          },
        });
        return;
      }

      const content = response.content ?? '';
      const currentChunkNumber = response.lastChunkNumber ?? 0;
      const processorState = processorRef.current.getState();
      const recoveryWouldRegress =
        currentChunkNumber < processorState.lastProcessedChunk ||
        content.length < processorState.accumulatedLength;
      const recoveryMadeProgress =
        currentChunkNumber > processorState.lastProcessedChunk ||
        content.length > processorState.accumulatedLength;

      if (response.complete) {
        if (recoveryWouldRegress) {
          const finalContent = processorRef.current.finalize();
          onStreamCompleteRef.current(finalContent.text, finalContent.blocks);
        } else {
          const blocks = parseResponseContent(content);
          onStreamCompleteRef.current(content, blocks);
        }
        finalizeActiveResponse();
        return;
      }

      // Recovery only repairs missing content. An unchanged or regressive
      // snapshot is ignored and never reclassified as a stalled response.
      if (!recoveryMadeProgress || recoveryWouldRegress) {
        return;
      }

      const drainedComplete = processorRef.current.applyRecoveryContent(
        content,
        currentChunkNumber,
      );

      if (drainedComplete) {
        const finalContent = processorRef.current.finalize();
        onStreamCompleteRef.current(finalContent.text, finalContent.blocks);
        finalizeActiveResponse();
      } else {
        const blocks = parseResponseContent(content);
        onContentUpdateRef.current(content, blocks);
        resetWatchdog();
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'StreamOrchestrator',
          action: 'gapRecovery',
          conversationId: state.conversationId ?? 'unknown',
        },
      });
    } finally {
      const currentState = stateRef.current;
      if (currentState.recoveryToken === recoveryToken) {
        currentState.isRecovering = false;
        currentState.recoveryToken = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalizeActiveResponse, refetchConversation, resetWatchdog]);

  const scheduleGapRecovery = useCallback(() => {
    const state = stateRef.current;
    if (!state.responseId || gapTimeoutRef.current) {
      return;
    }

    gapTimeoutRef.current = scheduler.schedule(async () => {
      gapTimeoutRef.current = null;
      await handleGapRecovery();
    }, GAP_TIMEOUT_MS);
  }, [handleGapRecovery, scheduler]);

  const processChunk = useCallback(
    (chunk: RawChunk) => {
      const state = stateRef.current;

      // Ignore idle traffic. We only stream for an explicitly started response.
      if (!state.responseId || chunk.responseId !== state.responseId) {
        return;
      }

      // Any chunk for the active response counts as liveness progress.
      resetWatchdog();

      const result = processorRef.current.addChunk(chunk);

      if (result.status === 'complete') {
        clearGapTimeout();
        onStreamCompleteRef.current(result.content.text, result.content.blocks);
        finalizeActiveResponse();
        return;
      }

      switch (result.status) {
        case 'processed':
          clearGapTimeout();
          onContentUpdateRef.current(result.content.text, result.content.blocks);
          break;

        case 'gap_detected':
          scheduleGapRecovery();
          break;

        case 'buffered':
          // Chunk was buffered (duplicate or out-of-order), no action needed
          break;
      }
    },
    [clearGapTimeout, finalizeActiveResponse, resetWatchdog, scheduleGapRecovery],
  );

  const startStream = useCallback(
    (conversationId: string, responseId?: string, recovery?: StreamRecovery) => {
      const state = stateRef.current;
      const hasActiveResponse = responseId != null;

      if (subscriptionRef.current && state.conversationId === conversationId) {
        // Same conversation: reuse subscription, reset processor for new response
        clearTimers();
        processorRef.current.reset();
        noProgressCountRef.current = 0;
        state.responseId = responseId ?? null;
        state.isRecovering = false;
        state.recoveryToken = null;
        setIsStreaming(hasActiveResponse);
      } else {
        // New subscription
        processorRef.current.reset();
        clearTimers();

        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }

        state.conversationId = conversationId;
        state.responseId = responseId ?? null;
        noProgressCountRef.current = 0;
        state.isRecovering = false;
        state.recoveryToken = null;
        setIsStreaming(hasActiveResponse);

        const observable = client.subscribe<{
          driveSubscribeToConversation: DriveResponseChunk;
        }>({
          query: DriveSubscribeToConversationDocument,
          variables: { conversationId },
        });

        const subscription = observable.subscribe({
          next: ({ data, errors }) => {
            const chunk = data?.driveSubscribeToConversation;

            if (errors?.length) {
              Sentry.captureException(
                new Error('[StreamOrchestrator] GraphQL subscription errors'),
                {
                  extra: { errors },
                  tags: { component: 'StreamOrchestrator' },
                },
              );

              // Treat GraphQL errors without a chunk payload as a terminal subscription failure.
              if (!chunk) {
                clearTimers();
                processorRef.current.reset();
                state.responseId = null;
                state.isRecovering = false;
                state.recoveryToken = null;
                onErrorRef.current({
                  type: 'subscription',
                  message: getSubscriptionErrorMessage(errors),
                });
                setIsStreaming(false);
                return;
              }
            }

            if (chunk) {
              try {
                processChunk({
                  chunkNumber: chunk.chunkNumber,
                  content: chunk.content ?? '',
                  complete: chunk.complete,
                  responseId: chunk.responseId,
                  conversationId: chunk.conversationId,
                });
              } catch (chunkError) {
                const streamError: StreamError = {
                  type: 'subscription',
                  message: 'Failed to process stream chunk',
                  originalError: chunkError instanceof Error ? chunkError : undefined,
                };
                Sentry.captureException(chunkError, {
                  tags: {
                    component: 'StreamOrchestrator',
                    action: 'processChunk',
                    conversationId: state.conversationId ?? 'unknown',
                  },
                });

                onErrorRef.current(streamError);
                clearTimers();
                processorRef.current.reset();
                state.responseId = null;
                state.isRecovering = false;
                state.recoveryToken = null;
                setIsStreaming(false);
              }
            }
          },
          error: (error) => {
            Sentry.captureException(error, {
              tags: {
                component: 'StreamOrchestrator',
                action: 'subscriptionError',
                conversationId: state.conversationId ?? 'unknown',
              },
            });

            const streamError: StreamError = {
              type: 'subscription',
              message: error instanceof Error ? error.message : 'Subscription error',
              originalError: error instanceof Error ? error : undefined,
            };
            onErrorRef.current(streamError);
            clearTimers();
            processorRef.current.reset();
            state.responseId = null;
            state.isRecovering = false;
            state.recoveryToken = null;
            setIsStreaming(false);
          },
          // Subscription transport closed. If we haven't already delivered a
          // completion (which nulls responseId), finalize accumulated content
          // so the UI doesn't hang in a streaming state.
          complete: () => {
            if (state.responseId && !state.isRecovering) {
              try {
                clearTimers();
                const { text, blocks } = processorRef.current.finalize();
                onStreamCompleteRef.current(text, blocks);
              } catch (completeError) {
                Sentry.captureException(completeError, {
                  tags: {
                    component: 'StreamOrchestrator',
                    action: 'subscriptionComplete',
                    conversationId: state.conversationId ?? 'unknown',
                  },
                });

                const streamError: StreamError = {
                  type: 'subscription',
                  message: 'Failed to finalize stream content',
                  originalError: completeError instanceof Error ? completeError : undefined,
                };
                onErrorRef.current(streamError);
              } finally {
                processorRef.current.reset();
                state.responseId = null;
                state.isRecovering = false;
                state.recoveryToken = null;
                setIsStreaming(false);
              }
            }
          },
        });

        subscriptionRef.current = subscription;
      }

      if (recovery) {
        const drainedComplete = processorRef.current.applyRecoveryContent(
          recovery.content,
          recovery.lastChunkNumber,
        );

        if (drainedComplete) {
          const finalContent = processorRef.current.finalize();
          onStreamCompleteRef.current(finalContent.text, finalContent.blocks);
          finalizeActiveResponse();
          return;
        } else {
          onContentUpdateRef.current(recovery.content, parseResponseContent(recovery.content));
        }
      }

      if (hasActiveResponse) {
        resetWatchdog();
      }
    },
    [
      client,
      clearTimers,
      finalizeActiveResponse,
      getSubscriptionErrorMessage,
      processChunk,
      resetWatchdog,
    ],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
      if (gapTimeoutRef.current) {
        scheduler.cancel(gapTimeoutRef.current);
      }
      if (watchdogTimeoutRef.current) {
        scheduler.cancel(watchdogTimeoutRef.current);
      }
    };
  }, [scheduler]);

  return {
    startStream,
    stopStream,
    isStreaming,
  };
};
